import { BehaviorSubject } from 'rxjs';

export class StorageService {
  protected subjects: { [key: string]: BehaviorSubject<any> } = {};
  protected repeats: { [key: string]: NodeJS.Timer } = {};
  protected storage: Storage;

  constructor(storage: Storage) {
    this.storage = storage;
  }

  /**
   * Creates Observable from storage item.
   * @param key Unique key in storage
   * @param defaultValue Default value to return if no value has been set
   * @param repeatInterval Defines in seconds how often storage should be checked for updates.
   * If omitted, then the returned Observable does not emit automatically between tabs.
   */
  select<T>(key: string, defaultValue: any = null, repeatInterval: number = 0): BehaviorSubject<T> {
    if (this.subjects.hasOwnProperty(key)) {
      return this.subjects[key];
    }

    if (!this.storage.getItem(key) && defaultValue) {
      this.storage.setItem(key, JSON.stringify(defaultValue));
    }

    const value = this.getStorageValue(key, defaultValue);
    this.subjects[key] = new BehaviorSubject<T>(value);

    if (repeatInterval > 0) {
      if (this.repeats.hasOwnProperty(key)) {
        clearInterval(this.repeats[key]);
      }

      this.repeats[key] = setInterval(() => this.emitDistinct(key, defaultValue), repeatInterval);
    }

    return this.subjects[key];
  }

  set(key: string, value: any): void {
    this.storage.setItem(key, JSON.stringify(value));

    if (this.subjects.hasOwnProperty(key)) {
      this.subjects[key].next(value);
    }
  }

  remove(...keys: string[]): void {
    for (const key of keys) {
      this.storage.removeItem(key);

      if (this.subjects.hasOwnProperty(key)) {
        this.subjects[key].next(null);
      }
    }
  }

  private getStorageValue(key: string, defaultValue?: string) {
    const item = this.storage.getItem(key);
    if (!item) {
      return defaultValue;
    }

    try {
      return JSON.parse(item);
    } catch (e) {
      return item;
    }
  }

  private emitDistinct(key: string, defaultValue: string) {
    const value = this.getStorageValue(key, defaultValue);
    if (this.subjects[key].value !== value) {
      this.subjects[key].next(value);
    }
  }
}
